import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import {
  IconCalendarDays,
  IconClock,
  IconPlus,
  IconCheck,
  IconX,
  IconDownload,
  IconSearch,
  IconUsers,
  IconTrash
} from '../components/Icons';
import api from '../services/api';
import toast from 'react-hot-toast';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Helper para calcular todos los días YYYY-MM-DD entre dos fechas
const getDaysBetween = (startStr: string, endStr: string): string[] => {
  const list: string[] = [];
  if (!startStr) return list;
  const s = new Date(startStr);
  const e = endStr ? new Date(endStr) : new Date(startStr);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return list;

  const cur = new Date(s);
  while (cur <= e) {
    list.push(cur.toISOString().split('T')[0]);
    cur.setDate(cur.getDate() + 1);
  }
  return list;
};

export interface PermissionItem {
  id: string;
  userId: string;
  type: 'CITA_MEDICA' | 'CALAMIDAD_FAMILIAR' | 'VACACIONES' | 'OTRO';
  customReason?: string;
  startDate: string;
  endDate: string;
  daysRequested: number;
  permissionDays: string[];
  repositionDays: string[];
  repositionNotes?: string;
  status: 'PENDIENTE_NICOLAS' | 'APROBADO_NICOLAS' | 'EN_RADAR_ALEJANDRA' | 'RECHAZADO';
  nicolasApprovedAt?: string;
  nicolasNotes?: string;
  alejandraSeenAt?: string;
  alejandraNotes?: string;
  salaryBase?: number;
  calculatedValue?: number;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    position?: string;
    avatar?: string;
    contractType?: string;
    baseSalary?: number;
  };
}

export interface OvertimeItem {
  id: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  hours: number;
  reason: string;
  status: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  approvedById?: string;
  approvedAt?: string;
  approvalNotes?: string;
  diurnaHours: number;
  nocturnaHours: number;
  festivaDiurnaHours: number;
  festivaNocturnaHours: number;
  hourlyRate: number;
  totalAmount: number;
  createdAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role?: string;
    position?: string;
    avatar?: string;
  };
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

const PermisosHorasExtra: React.FC = () => {
  const { user } = useAuth();

  // Tab activo: 'PERMISOS' | 'HORAS_EXTRA'
  const [activeTab, setActiveTab] = useState<'PERMISOS' | 'HORAS_EXTRA'>('PERMISOS');

  // Permisos
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [permSearch, setPermSearch] = useState('');
  const [permStatusFilter, setPermStatusFilter] = useState('TODAS');
  const [permTypeFilter, setPermTypeFilter] = useState('TODOS');

  // Horas Extras
  const [overtimes, setOvertimes] = useState<OvertimeItem[]>([]);
  const [loadingOvertime, setLoadingOvertime] = useState(true);
  const [overtimeStatusFilter, setOvertimeStatusFilter] = useState('TODAS');

  // Lista de miembros del equipo (para selectores de Admin)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Métricas
  const [permStats, setPermStats] = useState({
    pendingNicolasCount: 0,
    inRadarAlejandraCount: 0,
    approvedThisYearCount: 0,
    totalDaysThisYear: 0,
    collaboratorsCount: 0
  });

  const [overtimeStats, setOvertimeStats] = useState({
    pendingCount: 0,
    approvedThisMonthCount: 0,
    totalHoursThisMonth: 0,
    totalAmountThisMonth: 0,
    collaboratorsCount: 0
  });

  // Roles clave
  const userEmail = (user?.email || '').toLowerCase();
  const isNicolas = userEmail === 'fernandeznicolas1005@gmail.com';
  const isAlejandra = userEmail === 'contabilidadzoikagency@gmail.com';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'CUENTAS';

  // Modal Radicar Permiso
  const [showPermModal, setShowPermModal] = useState(false);
  const [targetUserId, setTargetUserId] = useState<string>('');
  const [overtimeTargetUserId, setOvertimeTargetUserId] = useState<string>('');
  const [filterCollaborator, setFilterCollaborator] = useState<string>('');
  const [permType, setPermType] = useState<'CITA_MEDICA' | 'CALAMIDAD_FAMILIAR' | 'VACACIONES' | 'OTRO'>('CITA_MEDICA');
  const [customReason, setCustomReason] = useState('');
  const [permStartDate, setPermStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [permEndDate, setPermEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPermDays, setSelectedPermDays] = useState<string[]>([new Date().toISOString().split('T')[0]]);

  // Reposición en Modal
  const [hasReposition, setHasReposition] = useState(true);
  const [repStartDate, setRepStartDate] = useState('');
  const [repEndDate, setRepEndDate] = useState('');
  const [selectedRepDays, setSelectedRepDays] = useState<string[]>([]);
  const [repositionNotes, setRepositionNotes] = useState('');
  const [submittingPerm, setSubmittingPerm] = useState(false);

  // Calendario 1: Días de Permiso
  const [cal1Month, setCal1Month] = useState(new Date().getMonth());
  const [cal1Year, setCal1Year] = useState(new Date().getFullYear());

  // Calendario 2: Días de Reposición
  const [cal2Month, setCal2Month] = useState(new Date().getMonth());
  const [cal2Year, setCal2Year] = useState(new Date().getFullYear());

  // Modal Anotar Horas Extras
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeForm, setOvertimeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '18:00',
    endTime: '20:30',
    reason: ''
  });
  const [submittingOvertime, setSubmittingOvertime] = useState(false);

  // Modal Dictamen Nicolás (Permisos)
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [targetPermId, setTargetPermId] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<'APROBADO' | 'RECHAZADO'>('APROBADO');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Modal Radar Alejandra (Permisos)
  const [radarModalOpen, setRadarModalOpen] = useState(false);
  const [radarTargetPermId, setRadarTargetPermId] = useState<string | null>(null);
  const [radarNotes, setRadarNotes] = useState('');
  const [submittingRadar, setSubmittingRadar] = useState(false);

  // Modal Dictamen Nicolás / Admin (Horas Extras)
  const [overtimeReviewModalOpen, setOvertimeReviewModalOpen] = useState(false);
  const [targetOvertimeId, setTargetOvertimeId] = useState<string | null>(null);
  const [overtimeReviewAction, setOvertimeReviewAction] = useState<'APROBADO' | 'RECHAZADO'>('APROBADO');
  const [overtimeReviewNotes, setOvertimeReviewNotes] = useState('');
  const [submittingOvertimeReview, setSubmittingOvertimeReview] = useState(false);

  // Cargar Permisos
  const fetchPermissions = async () => {
    try {
      setLoadingPerms(true);
      const res = await api.get('/permissions');
      setPermissions(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      console.error('Error al cargar permisos:', e);
      toast.error('No fue posible cargar los permisos');
    } finally {
      setLoadingPerms(false);
    }
  };

  // Cargar Stats Permisos
  const fetchPermStats = async () => {
    try {
      const res = await api.get('/permissions/stats/summary');
      if (res.data) setPermStats(res.data);
    } catch (e) {
      console.error('Error al cargar métricas de permisos:', e);
    }
  };

  // Cargar Horas Extras
  const fetchOvertimes = async () => {
    try {
      setLoadingOvertime(true);
      const res = await api.get('/overtime');
      setOvertimes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error al cargar horas extras:', e);
      toast.error('No fue posible cargar horas extras');
    } finally {
      setLoadingOvertime(false);
    }
  };

  // Cargar Stats Horas Extras
  const fetchOvertimeStats = async () => {
    try {
      const res = await api.get('/overtime/stats/summary');
      if (res.data) setOvertimeStats(res.data);
    } catch (e) {
      console.error('Error al cargar métricas de horas extras:', e);
    }
  };

  // Cargar miembros para asignación
  const fetchTeamMembers = async () => {
    try {
      const res = await api.get('/users');
      if (Array.isArray(res.data)) {
        setTeamMembers(res.data);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchPermissions();
    fetchPermStats();
    fetchOvertimes();
    fetchOvertimeStats();
    fetchTeamMembers();
  }, []);

  // Sincronizar fechas escritas con días seleccionados en Calendario 1
  const handlePermRangeChange = (start: string, end: string) => {
    setPermStartDate(start);
    setPermEndDate(end);
    if (start) {
      const days = getDaysBetween(start, end || start);
      setSelectedPermDays(days);
      const s = new Date(start);
      if (!isNaN(s.getTime())) {
        setCal1Month(s.getMonth());
        setCal1Year(s.getFullYear());
      }
    }
  };

  // Sincronizar fechas de reposición con Calendario 2
  const handleRepRangeChange = (start: string, end: string) => {
    setRepStartDate(start);
    setRepEndDate(end);
    if (start) {
      const days = getDaysBetween(start, end || start);
      setSelectedRepDays(days);
      const s = new Date(start);
      if (!isNaN(s.getTime())) {
        setCal2Month(s.getMonth());
        setCal2Year(s.getFullYear());
      }
    }
  };

  // Helpers para generar celdas del mes
  const generateMonthGrid = (year: number, month: number) => {
    const firstDayIndex = new Date(year, month, 1).getDay();
    const adjustedFirstDay = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: { dateStr: string; dayNum: number; isCurrentMonth: boolean }[] = [];

    for (let i = 0; i < adjustedFirstDay; i++) {
      cells.push({ dateStr: '', dayNum: 0, isCurrentMonth: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const mm = String(month + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      cells.push({
        dateStr: `${year}-${mm}-${dd}`,
        dayNum: d,
        isCurrentMonth: true
      });
    }

    return cells;
  };

  // Toggle de selección en Calendario 1 (Permisos)
  const togglePermDay = (dateStr: string) => {
    if (!dateStr) return;
    setSelectedPermDays(prev => {
      let updated: string[];
      if (prev.includes(dateStr)) {
        updated = prev.filter(d => d !== dateStr);
      } else {
        updated = [...prev, dateStr].sort();
      }
      if (updated.length > 0) {
        setPermStartDate(updated[0]);
        setPermEndDate(updated[updated.length - 1]);
      }
      return updated;
    });
  };

  // Toggle de selección en Calendario 2 (Reposición)
  const toggleRepDay = (dateStr: string) => {
    if (!dateStr) return;
    setSelectedRepDays(prev => {
      let updated: string[];
      if (prev.includes(dateStr)) {
        updated = prev.filter(d => d !== dateStr);
      } else {
        updated = [...prev, dateStr].sort();
      }
      if (updated.length > 0) {
        setRepStartDate(updated[0]);
        setRepEndDate(updated[updated.length - 1]);
      }
      return updated;
    });
  };

  // Manejo de Envío de Permiso
  const handleCreatePermission = async (e: React.FormEvent) => {
    e.preventDefault();

    // Si no ha hecho clic en el calendario pero puso fecha inicio, autogenerar
    let finalPermDays = [...selectedPermDays];
    if (finalPermDays.length === 0 && permStartDate) {
      finalPermDays = getDaysBetween(permStartDate, permEndDate || permStartDate);
      setSelectedPermDays(finalPermDays);
    }

    if (finalPermDays.length === 0) {
      toast.error('Indica al menos una fecha de ausencia');
      return;
    }

    try {
      setSubmittingPerm(true);
      await api.post('/permissions', {
        targetUserId: targetUserId || undefined,
        type: permType,
        customReason: customReason.trim() || undefined,
        daysRequested: finalPermDays.length,
        permissionDays: finalPermDays,
        repositionDays: hasReposition ? selectedRepDays : [],
        repositionNotes: repositionNotes.trim() || (hasReposition ? undefined : 'No aplica reposición'),
        startDate: finalPermDays[0],
        endDate: finalPermDays[finalPermDays.length - 1]
      });

      toast.success('Permiso radicado exitosamente. Notificado a Nicolás Fernández');
      setShowPermModal(false);
      // Reset
      setSelectedPermDays([new Date().toISOString().split('T')[0]]);
      setSelectedRepDays([]);
      setCustomReason('');
      setRepositionNotes('');
      setPermType('CITA_MEDICA');
      setTargetUserId('');

      fetchPermissions();
      fetchPermStats();
    } catch (err: any) {
      console.error('Error al radicar permiso:', err);
      toast.error(err.response?.data?.error || 'Error al radicar el permiso');
    } finally {
      setSubmittingPerm(false);
    }
  };

  // Manejo de Envío de Horas Extras
  const handleCreateOvertime = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!overtimeForm.reason.trim()) {
      toast.error('Debes especificar el motivo de las horas extras');
      return;
    }

    try {
      setSubmittingOvertime(true);
      await api.post('/overtime', { ...overtimeForm, targetUserId: overtimeTargetUserId || undefined });
      toast.success('Horas extras registradas. Notificado a Nicolás Fernández');
      setShowOvertimeModal(false);
      setOvertimeForm({
        date: new Date().toISOString().split('T')[0],
        startTime: '18:00',
        endTime: '20:30',
        reason: ''
      });
      fetchOvertimes();
      fetchOvertimeStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al registrar horas extras');
    } finally {
      setSubmittingOvertime(false);
    }
  };

  // Nicolás Aprobación / Rechazo (Permisos)
  const handleNicolasReview = async () => {
    if (!targetPermId) return;
    try {
      setSubmittingReview(true);
      await api.put(`/permissions/${targetPermId}/nicolas-review`, {
        action: reviewAction,
        notes: reviewNotes.trim() || undefined
      });

      toast.success(
        reviewAction === 'APROBADO'
          ? 'Permiso aprobado por Nicolás y enviado al radar de Alejandra'
          : 'Permiso marcado como no aprobado'
      );
      setReviewModalOpen(false);
      setTargetPermId(null);
      setReviewNotes('');
      fetchPermissions();
      fetchPermStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al procesar dictamen');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Alejandra Confirmación en Radar (Permisos)
  const handleAlejandraRadarConfirm = async () => {
    if (!radarTargetPermId) return;
    try {
      setSubmittingRadar(true);
      await api.put(`/permissions/${radarTargetPermId}/alejandra-radar`, {
        notes: radarNotes.trim() || 'Verificado en radar contable y talento humano'
      });
      toast.success('Permiso confirmado en tu radar contable');
      setRadarModalOpen(false);
      setRadarTargetPermId(null);
      setRadarNotes('');
      fetchPermissions();
      fetchPermStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al confirmar en radar');
    } finally {
      setSubmittingRadar(false);
    }
  };

  // Dictamen de Horas Extras
  const handleOvertimeReview = async () => {
    if (!targetOvertimeId) return;
    try {
      setSubmittingOvertimeReview(true);
      await api.put(`/overtime/${targetOvertimeId}/review`, {
        action: overtimeReviewAction,
        approvalNotes: overtimeReviewNotes.trim() || undefined
      });
      toast.success(
        overtimeReviewAction === 'APROBADO'
          ? 'Horas extras aprobadas y enviadas a liquidación de Alejandra'
          : 'Horas extras no aprobadas'
      );
      setOvertimeReviewModalOpen(false);
      setTargetOvertimeId(null);
      setOvertimeReviewNotes('');
      fetchOvertimes();
      fetchOvertimeStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al dictaminar horas extras');
    } finally {
      setSubmittingOvertimeReview(false);
    }
  };

  // Anular Solicitud propia
  const handleDeletePermission = async (id: string) => {
    if (!window.confirm('¿Deseas anular esta solicitud de permiso?')) return;
    try {
      await api.delete(`/permissions/${id}`);
      toast.success('Solicitud anulada');
      fetchPermissions();
      fetchPermStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular solicitud');
    }
  };

  // Descargar PDF de Permiso de manera segura en todos los navegadores
  const handleDownloadPermPDF = async (p: PermissionItem) => {
    try {
      const response = await api.get(`/permissions/${p.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Permiso_Zoik_${p.user?.firstName || 'Tripulante'}_${p.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error('No se pudo generar el documento PDF');
    }
  };

  // Descargar PDF de Horas Extras de manera segura
  const handleDownloadOvertimePDF = async (o: OvertimeItem) => {
    try {
      const response = await api.get(`/overtime/${o.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HorasExtras_Zoik_${o.user?.firstName || 'Tripulante'}_${o.id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error('No se pudo generar el documento PDF');
    }
  };

  // Permisos Filtrados
  const filteredPermissions = useMemo(() => {
    return permissions.filter(p => {
      if (permSearch.trim()) {
        const query = permSearch.toLowerCase();
        const fullName = `${p.user?.firstName || ''} ${p.user?.lastName || ''}`.toLowerCase();
        const customR = (p.customReason || '').toLowerCase();
        if (!fullName.includes(query) && !customR.includes(query) && !p.type.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (permStatusFilter !== 'TODAS') {
        if (p.status !== permStatusFilter) return false;
      }
      if (permTypeFilter !== 'TODOS') {
        if (p.type !== permTypeFilter) return false;
      }
      return true;
    });
  }, [permissions, permSearch, permStatusFilter, permTypeFilter]);

  // Horas Extras Filtradas
  const filteredOvertimes = useMemo(() => {
    return overtimes.filter(o => {
      if (overtimeStatusFilter !== 'TODAS') {
        if (o.status !== overtimeStatusFilter) return false;
      }
      return true;
    });
  }, [overtimes, overtimeStatusFilter]);

  // Formato de Tipo
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'CITA_MEDICA':
        return { label: 'Cita Médica', bg: 'rgba(2, 132, 199, 0.12)', border: 'rgba(2, 132, 199, 0.25)', color: '#0284c7', icon: '🩺' };
      case 'CALAMIDAD_FAMILIAR':
        return { label: 'Calamidad Familiar', bg: 'rgba(217, 119, 6, 0.12)', border: 'rgba(217, 119, 6, 0.25)', color: '#d97706', icon: '🏠' };
      case 'VACACIONES':
        return { label: 'Vacaciones', bg: 'rgba(5, 150, 105, 0.12)', border: 'rgba(5, 150, 105, 0.25)', color: '#059669', icon: '🌴' };
      case 'OTRO':
      default:
        return { label: 'Otro Motivo', bg: 'rgba(100, 116, 139, 0.12)', border: 'rgba(100, 116, 139, 0.25)', color: '#475569', icon: '✍️' };
    }
  };

  // Formato de Estado
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDIENTE_NICOLAS':
        return { label: 'Pendiente Aprobación (Nicolás)', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.3)', color: '#b45309', icon: '⏳' };
      case 'APROBADO_NICOLAS':
        return { label: 'Aprobado Nicolás · En Radar de Alejandra', bg: 'rgba(2, 132, 199, 0.14)', border: 'rgba(2, 132, 199, 0.35)', color: '#0284c7', icon: '📌' };
      case 'EN_RADAR_ALEJANDRA':
        return { label: 'Verificado en Radar de Alejandra', bg: 'rgba(5, 150, 105, 0.14)', border: 'rgba(5, 150, 105, 0.35)', color: '#047857', icon: '✅' };
      case 'RECHAZADO':
        return { label: 'No Aprobado', bg: 'rgba(220, 38, 38, 0.12)', border: 'rgba(220, 38, 38, 0.3)', color: '#b91c1c', icon: '❌' };
      default:
        return { label: status, bg: '#f1f5f9', border: '#cbd5e1', color: '#64748b', icon: '•' };
    }
  };

  return (
    <div className="zoik-page-container" style={{
      display: 'flex',
      height: '100vh',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflow: 'hidden',
      background: 'radial-gradient(at 0% 0%, rgba(0, 112, 243, 0.07) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(38, 136, 201, 0.08) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(99, 102, 241, 0.06) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%), #edf2f8',
      fontFamily: '"Montserrat", sans-serif'
    }}>
      <Sidebar />

      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        minWidth: 0,
        padding: '28px 36px'
      }}>
        <div style={{ maxWidth: '1440px', margin: '0 auto', width: '100%', paddingBottom: '90px' }}>
        {/* Encabezado Principal */}
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <div style={{
                width: '42px',
                height: '42px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 8px 20px rgba(0, 112, 243, 0.3)'
              }}>
                <IconCalendarDays size={22} color="white" />
              </div>
              <h1 style={{ fontSize: '26px', fontWeight: 900, color: '#0f2a4a', margin: 0, letterSpacing: '-0.5px' }}>
                Permisos y Horas Extra
              </h1>
            </div>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0, fontWeight: 500 }}>
              Radicación de ausencias con calendario, reposición pactada, radar contable y liquidación de horas extras.
            </p>
          </div>

          {/* Botones de Acción Rápida */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowOvertimeModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '11px 18px',
                borderRadius: '14px',
                background: 'rgba(255, 255, 255, 0.85)',
                border: '1.5px solid rgba(15, 42, 74, 0.12)',
                color: '#0f2a4a',
                fontSize: '12.5px',
                fontWeight: 800,
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 14px rgba(15, 42, 74, 0.04)',
                transition: 'all 0.2s'
              }}
            >
              <IconClock size={16} color="#0070f3" />
              <span>+ Anotar Horas Extras</span>
            </button>

            <button
              onClick={() => setShowPermModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '11px 20px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)',
                border: 'none',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(0, 112, 243, 0.35)',
                transition: 'all 0.2s'
              }}
            >
              <IconPlus size={16} color="white" />
              <span>+ Radicar Solicitud de Permiso</span>
            </button>
          </div>
        </header>

        {/* Tarjetas de Métricas Estadísticas (Liquid Glass) */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1.5px solid rgba(255, 255, 255, 0.9)',
            padding: '18px 22px',
            boxShadow: '0 10px 28px rgba(15, 42, 74, 0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Pendientes de Nicolás
              </span>
              <span style={{ fontSize: '18px' }}>⏳</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1 }}>
              {permStats.pendingNicolasCount}
            </div>
            <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '6px' }}>
              Permisos en espera de dictamen ejecutivo
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1.5px solid rgba(255, 255, 255, 0.9)',
            padding: '18px 22px',
            boxShadow: '0 10px 28px rgba(15, 42, 74, 0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                En Radar de Alejandra
              </span>
              <span style={{ fontSize: '18px' }}>📌</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1 }}>
              {permStats.inRadarAlejandraCount}
            </div>
            <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '6px' }}>
              Aprobados en control de RRHH y nómina
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1.5px solid rgba(255, 255, 255, 0.9)',
            padding: '18px 22px',
            boxShadow: '0 10px 28px rgba(15, 42, 74, 0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Horas Extras Mes
              </span>
              <span style={{ fontSize: '18px' }}>⏰</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#047857', lineHeight: 1 }}>
              {overtimeStats.totalHoursThisMonth.toFixed(1)} hrs
            </div>
            <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '6px' }}>
              ${overtimeStats.totalAmountThisMonth.toLocaleString('es-CO')} COP liquidados
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1.5px solid rgba(255, 255, 255, 0.9)',
            padding: '18px 22px',
            boxShadow: '0 10px 28px rgba(15, 42, 74, 0.05)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Días Concedidos (Año)
              </span>
              <span style={{ fontSize: '18px' }}>🌴</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1 }}>
              {permStats.totalDaysThisYear} días
            </div>
            <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '6px' }}>
              Distribuidos en {permStats.collaboratorsCount} colaboradores
            </div>
          </div>
        </section>

        {/* Barra de Pestañas (Tab Switcher) */}
        <div style={{
          display: 'flex',
          gap: '8px',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(16px)',
          padding: '6px',
          borderRadius: '16px',
          border: '1.5px solid rgba(255, 255, 255, 0.85)',
          marginBottom: '24px',
          width: 'fit-content'
        }}>
          <button
            onClick={() => setActiveTab('PERMISOS')}
            style={{
              padding: '10px 22px',
              borderRadius: '12px',
              border: 'none',
              background: activeTab === 'PERMISOS' ? 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)' : 'transparent',
              color: activeTab === 'PERMISOS' ? '#ffffff' : '#0f2a4a',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: activeTab === 'PERMISOS' ? '0 4px 14px rgba(0, 112, 243, 0.28)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            <span>📋 Permisos Laborales & Vacaciones</span>
            <span style={{
              fontSize: '11px',
              background: activeTab === 'PERMISOS' ? 'rgba(255,255,255,0.25)' : 'rgba(15,42,74,0.08)',
              padding: '2px 8px',
              borderRadius: '10px'
            }}>
              {permissions.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('HORAS_EXTRA')}
            style={{
              padding: '10px 22px',
              borderRadius: '12px',
              border: 'none',
              background: activeTab === 'HORAS_EXTRA' ? 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)' : 'transparent',
              color: activeTab === 'HORAS_EXTRA' ? '#ffffff' : '#0f2a4a',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: activeTab === 'HORAS_EXTRA' ? '0 4px 14px rgba(0, 112, 243, 0.28)' : 'none',
              transition: 'all 0.2s'
            }}
          >
            <span>⏰ Registro de Horas Extras</span>
            <span style={{
              fontSize: '11px',
              background: activeTab === 'HORAS_EXTRA' ? 'rgba(255,255,255,0.25)' : 'rgba(15,42,74,0.08)',
              padding: '2px 8px',
              borderRadius: '10px'
            }}>
              {overtimes.length}
            </span>
          </button>
        </div>

        {/* ========================================================================= */}
        {/* PESTAÑA 1: PERMISOS LABORALES & VACACIONES                                */}
        {/* ========================================================================= */}
        {activeTab === 'PERMISOS' && (
          <div>
            {/* Barra de Filtros */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.72)',
              backdropFilter: 'blur(16px)',
              borderRadius: '18px',
              border: '1.5px solid rgba(255, 255, 255, 0.85)',
              padding: '14px 18px',
              marginBottom: '20px',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffffff',
                border: '1px solid rgba(15, 42, 74, 0.12)',
                borderRadius: '12px',
                padding: '8px 14px',
                flex: '1 1 240px'
              }}>
                <IconSearch size={16} color="#64748b" />
                <input
                  type="text"
                  placeholder="Buscar colaborador o motivo..."
                  value={permSearch}
                  onChange={e => setPermSearch(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    fontSize: '12.5px',
                    color: '#0f2a4a',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#64748b' }}>Estado:</span>
                <select
                  value={permStatusFilter}
                  onChange={e => setPermStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15, 42, 74, 0.12)',
                    background: '#ffffff',
                    fontSize: '12.5px',
                    color: '#0f2a4a',
                    fontWeight: 700,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <option value="TODAS">Todos los Estados</option>
                  <option value="PENDIENTE_NICOLAS">⏳ Pendiente de Nicolás</option>
                  <option value="APROBADO_NICOLAS">📌 En Radar de Alejandra</option>
                  <option value="EN_RADAR_ALEJANDRA">✅ Verificado en Radar</option>
                  <option value="RECHAZADO">❌ No Aprobado</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#64748b' }}>Tipo:</span>
                <select
                  value={permTypeFilter}
                  onChange={e => setPermTypeFilter(e.target.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15, 42, 74, 0.12)',
                    background: '#ffffff',
                    fontSize: '12.5px',
                    color: '#0f2a4a',
                    fontWeight: 700,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <option value="TODOS">Todos los Tipos</option>
                  <option value="CITA_MEDICA">🩺 Cita Médica</option>
                  <option value="CALAMIDAD_FAMILIAR">🏠 Calamidad Familiar</option>
                  <option value="VACACIONES">🌴 Vacaciones</option>
                  <option value="OTRO">✍️ Otro Motivo</option>
                </select>
              </div>
            </div>

            {/* Listado de Solicitudes */}
            {loadingPerms ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                Cargando solicitudes de permisos...
              </div>
            ) : filteredPermissions.length === 0 ? (
              <div style={{
                padding: '60px 20px',
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.65)',
                borderRadius: '20px',
                border: '1.5px dashed rgba(15, 42, 74, 0.15)',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📋</div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f2a4a' }}>No hay solicitudes de permiso registradas</div>
                <div style={{ fontSize: '12.5px', marginTop: '6px' }}>Haz clic en "+ Radicar Solicitud de Permiso" para registrar ausencias.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredPermissions.map(p => {
                  const typeBadge = getTypeBadge(p.type);
                  const statusBadge = getStatusBadge(p.status);
                  const isOwner = p.userId === (user?.id || (user as any)?.userId);

                  return (
                    <div
                      key={p.id}
                      style={{
                        background: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '20px',
                        border: '1.5px solid rgba(255, 255, 255, 0.95)',
                        padding: '20px 24px',
                        boxShadow: '0 8px 24px rgba(15, 42, 74, 0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px'
                      }}
                    >
                      {/* Cabecera del Permiso */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {p.user?.avatar ? (
                            <img
                              src={p.user.avatar}
                              alt={p.user.firstName}
                              style={{ width: '42px', height: '42px', borderRadius: '14px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '14px',
                              background: 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)',
                              color: 'white',
                              fontWeight: 900,
                              fontSize: '15px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {(p.user?.firstName || 'Z')[0]}
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>
                              {p.user?.firstName || 'Tripulante'} {p.user?.lastName || 'ZOIK'}
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 600 }}>
                              {p.user?.position || 'Tripulante ZOIK'} · {p.user?.email || ''}
                            </div>
                          </div>
                        </div>

                        {/* Badges de Tipo y Estado */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '20px',
                            background: typeBadge.bg,
                            border: `1px solid ${typeBadge.border}`,
                            color: typeBadge.color,
                            fontSize: '11.5px',
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span>{typeBadge.icon}</span>
                            <span>{typeBadge.label}</span>
                          </span>

                          <span style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            background: statusBadge.bg,
                            border: `1px solid ${statusBadge.border}`,
                            color: statusBadge.color,
                            fontSize: '11.5px',
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span>{statusBadge.icon}</span>
                            <span>{statusBadge.label}</span>
                          </span>
                        </div>
                      </div>

                      {/* Motivo Justificado */}
                      {p.customReason && (
                        <div style={{
                          background: 'rgba(15, 42, 74, 0.03)',
                          borderRadius: '12px',
                          padding: '10px 14px',
                          fontSize: '12.5px',
                          color: '#1e293b',
                          fontStyle: 'italic',
                          borderLeft: '3px solid #0070f3'
                        }}>
                          "{p.customReason}"
                        </div>
                      )}

                      {/* Cuadrículas: Días de Ausencia vs Días de Reposición */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
                        {/* Días de Permiso Solicitados */}
                        <div style={{
                          background: 'rgba(2, 132, 199, 0.05)',
                          borderRadius: '14px',
                          border: '1px solid rgba(2, 132, 199, 0.2)',
                          padding: '12px 14px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase' }}>
                              🗓️ Días de Permiso Solicitados
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: 900, background: '#0284c7', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>
                              {p.daysRequested} día(s)
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {p.permissionDays && p.permissionDays.length > 0 ? (
                              p.permissionDays.map(d => (
                                <span
                                  key={d}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    background: '#ffffff',
                                    border: '1px solid rgba(2, 132, 199, 0.3)',
                                    color: '#0f2a4a',
                                    padding: '3px 8px',
                                    borderRadius: '8px'
                                  }}
                                >
                                  {d}
                                </span>
                              ))
                            ) : (
                              <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                                Del {new Date(p.startDate).toLocaleDateString('es-CO')} al {new Date(p.endDate).toLocaleDateString('es-CO')}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Días de Reposición Pactados */}
                        <div style={{
                          background: 'rgba(5, 150, 105, 0.05)',
                          borderRadius: '14px',
                          border: '1px solid rgba(5, 150, 105, 0.2)',
                          padding: '12px 14px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#059669', textTransform: 'uppercase' }}>
                              🤝 Días de Reposición Pactados
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: 900, background: '#059669', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>
                              {p.repositionDays?.length || 0} día(s)
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {p.repositionDays && p.repositionDays.length > 0 ? (
                              p.repositionDays.map(d => (
                                <span
                                  key={d}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    background: '#ffffff',
                                    border: '1px solid rgba(5, 150, 105, 0.3)',
                                    color: '#065f46',
                                    padding: '3px 8px',
                                    borderRadius: '8px'
                                  }}
                                >
                                  {d}
                                </span>
                              ))
                            ) : (
                              <span style={{ fontSize: '11.5px', color: '#64748b', fontStyle: 'italic' }}>
                                {p.type === 'VACACIONES' ? 'No aplica (Vacaciones remuneradas)' : 'No requiere reposición'}
                              </span>
                            )}
                          </div>
                          {p.repositionNotes && (
                            <div style={{ fontSize: '11px', color: '#047857', marginTop: '6px', fontWeight: 600 }}>
                              Horario / Notas: {p.repositionNotes}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Barra Inferior: Firmas y Acciones */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px solid rgba(15, 42, 74, 0.08)',
                        paddingTop: '12px',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          Radicado el {new Date(p.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                          {p.nicolasApprovedAt && ` · Aprobado por Nicolás (${new Date(p.nicolasApprovedAt).toLocaleDateString('es-CO')})`}
                          {p.alejandraSeenAt && ` · En radar Alejandra (${new Date(p.alejandraSeenAt).toLocaleDateString('es-CO')})`}
                        </div>

                        {/* Botones de Acción */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => handleDownloadPermPDF(p)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 14px',
                              borderRadius: '10px',
                              border: '1px solid rgba(15, 42, 74, 0.12)',
                              background: '#ffffff',
                              color: '#0f2a4a',
                              fontSize: '11.5px',
                              fontWeight: 800,
                              cursor: 'pointer'
                            }}
                          >
                            <IconDownload size={14} color="#0070f3" />
                            <span>PDF Oficial</span>
                          </button>

                          {(isNicolas || isAdmin) && p.status === 'PENDIENTE_NICOLAS' && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setTargetPermId(p.id);
                                  setReviewAction('APROBADO');
                                  setReviewNotes('');
                                  setReviewModalOpen(true);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '8px 14px',
                                  borderRadius: '10px',
                                  border: 'none',
                                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                  color: 'white',
                                  fontSize: '11.5px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
                                }}
                              >
                                <IconCheck size={14} color="white" />
                                <span>Aprobar (Nicolás)</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setTargetPermId(p.id);
                                  setReviewAction('RECHAZADO');
                                  setReviewNotes('');
                                  setReviewModalOpen(true);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '8px 14px',
                                  borderRadius: '10px',
                                  border: '1px solid rgba(220, 38, 38, 0.2)',
                                  background: 'rgba(220, 38, 38, 0.08)',
                                  color: '#dc2626',
                                  fontSize: '11.5px',
                                  fontWeight: 800,
                                  cursor: 'pointer'
                                }}
                              >
                                <IconX size={14} color="#dc2626" />
                                <span>Rechazar</span>
                              </button>
                            </>
                          )}

                          {(isAlejandra || isAdmin) && p.status === 'APROBADO_NICOLAS' && (
                            <button
                              type="button"
                              onClick={() => {
                                setRadarTargetPermId(p.id);
                                setRadarNotes('');
                                setRadarModalOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '8px 14px',
                                borderRadius: '10px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                color: 'white',
                                fontSize: '11.5px',
                                fontWeight: 800,
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)'
                              }}
                            >
                              <span>📌 Confirmar en mi Radar</span>
                            </button>
                          )}

                          {(isOwner || isAdmin) && p.status === 'PENDIENTE_NICOLAS' && (
                            <button
                              type="button"
                              onClick={() => handleDeletePermission(p.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                padding: '6px'
                              }}
                              title="Anular Solicitud"
                            >
                              <IconTrash size={15} color="#94a3b8" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* PESTAÑA 2: REGISTRO DE HORAS EXTRAS                                       */}
        {/* ========================================================================= */}
        {activeTab === 'HORAS_EXTRA' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.72)',
              backdropFilter: 'blur(16px)',
              borderRadius: '18px',
              border: '1.5px solid rgba(255, 255, 255, 0.85)',
              padding: '14px 18px',
              marginBottom: '20px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a' }}>
                Total Horas Registradas: <span style={{ color: '#0070f3' }}>{overtimes.length}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#64748b' }}>Estado:</span>
                <select
                  value={overtimeStatusFilter}
                  onChange={e => setOvertimeStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15, 42, 74, 0.12)',
                    background: '#ffffff',
                    fontSize: '12.5px',
                    color: '#0f2a4a',
                    fontWeight: 700,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <option value="TODAS">Todos los Estados</option>
                  <option value="PENDIENTE">⏳ Pendientes</option>
                  <option value="APROBADO">✅ Aprobadas</option>
                  <option value="RECHAZADO">❌ Rechazadas</option>
                </select>
              </div>
            </div>

            {loadingOvertime ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
                Cargando registros de horas extras...
              </div>
            ) : filteredOvertimes.length === 0 ? (
              <div style={{
                padding: '60px 20px',
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.65)',
                borderRadius: '20px',
                border: '1.5px dashed rgba(15, 42, 74, 0.15)',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏰</div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f2a4a' }}>No hay horas extras registradas</div>
                <div style={{ fontSize: '12.5px', marginTop: '6px' }}>Haz clic en "+ Anotar Horas Extras" para registrar jornadas suplementarias.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {filteredOvertimes.map(o => {
                  return (
                    <div
                      key={o.id}
                      style={{
                        background: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '20px',
                        border: '1.5px solid rgba(255, 255, 255, 0.95)',
                        padding: '18px 22px',
                        boxShadow: '0 8px 24px rgba(15, 42, 74, 0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #059669 0%, #0f2a4a 100%)',
                            color: 'white',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px'
                          }}>
                            {o.user?.firstName ? o.user.firstName[0] : 'Z'}
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 900, color: '#0f2a4a' }}>
                              {o.user?.firstName} {o.user?.lastName}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              Fecha: <strong>{new Date(o.date).toLocaleDateString('es-CO')}</strong> · Horario: <strong>{o.startTime} - {o.endTime}</strong> ({o.hours} hrs)
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '11.5px',
                            fontWeight: 800,
                            background: o.status === 'APROBADO' ? 'rgba(5, 150, 105, 0.12)' : o.status === 'RECHAZADO' ? 'rgba(220, 38, 38, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                            color: o.status === 'APROBADO' ? '#047857' : o.status === 'RECHAZADO' ? '#b91c1c' : '#b45309',
                            border: `1px solid ${o.status === 'APROBADO' ? 'rgba(5, 150, 105, 0.3)' : o.status === 'RECHAZADO' ? 'rgba(220, 38, 38, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                          }}>
                            {o.status === 'APROBADO' ? '✅ Aprobado' : o.status === 'RECHAZADO' ? '❌ Rechazado' : '⏳ Pendiente'}
                          </span>

                          <span style={{ fontSize: '14px', fontWeight: 900, color: '#047857' }}>
                            ${Math.round(o.totalAmount || 0).toLocaleString('es-CO')} COP
                          </span>
                        </div>
                      </div>

                      <div style={{ fontSize: '12.5px', color: '#334155', background: 'rgba(15, 42, 74, 0.03)', padding: '8px 12px', borderRadius: '10px' }}>
                        Motivo: <strong>{o.reason}</strong>
                      </div>

                      {/* Desglose de Franjas */}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '11px', color: '#64748b' }}>
                        <span>Diurna: <strong>{o.diurnaHours || 0}h</strong></span>
                        <span>·</span>
                        <span>Nocturna (35%): <strong>{o.nocturnaHours || 0}h</strong></span>
                        <span>·</span>
                        <span>Festiva Diurna (75%): <strong>{o.festivaDiurnaHours || 0}h</strong></span>
                        <span>·</span>
                        <span>Festiva Nocturna (110%): <strong>{o.festivaNocturnaHours || 0}h</strong></span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid rgba(15,42,74,0.06)', paddingTop: '10px', alignItems: 'center' }}>
                        {/* Aprobación Nicolás o Admin para Horas Extras */}
                        {(isNicolas || isAdmin) && o.status === 'PENDIENTE' && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setTargetOvertimeId(o.id);
                                setOvertimeReviewAction('APROBADO');
                                setOvertimeReviewNotes('');
                                setOvertimeReviewModalOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#059669',
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              <IconCheck size={13} color="white" />
                              <span>Aprobar Horas</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setTargetOvertimeId(o.id);
                                setOvertimeReviewAction('RECHAZADO');
                                setOvertimeReviewNotes('');
                                setOvertimeReviewModalOpen(true);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid rgba(220, 38, 38, 0.2)',
                                background: 'rgba(220, 38, 38, 0.08)',
                                color: '#dc2626',
                                fontSize: '11px',
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              <IconX size={13} color="#dc2626" />
                              <span>Rechazar</span>
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          onClick={() => handleDownloadOvertimePDF(o)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(15, 42, 74, 0.12)',
                            background: '#ffffff',
                            color: '#0f2a4a',
                            fontSize: '11px',
                            fontWeight: 800,
                            cursor: 'pointer'
                          }}
                        >
                          <IconDownload size={13} color="#0070f3" />
                          <span>PDF Membretado</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </div>
      </main>

      {/* ========================================================================= */}
      {/* MODAL: RADICAR SOLICITUD DE PERMISO (AMIGABLE, RANGOS + CALENDARIOS)      */}
      {/* ========================================================================= */}
      {showPermModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.65)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(28px)',
            borderRadius: '24px',
            border: '1.5px solid rgba(255, 255, 255, 0.98)',
            boxShadow: '0 24px 60px rgba(15, 42, 74, 0.2)',
            width: '100%',
            maxWidth: '860px',
            maxHeight: '92vh',
            overflowY: 'auto',
            padding: '28px 32px'
          }}>
            {/* Header del Modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f2a4a', margin: 0 }}>
                  Radicar Solicitud de Permiso
                </h2>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>
                  Elige las fechas con el selector o haz clic directamente sobre los días del calendario.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPermModal(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  color: '#64748b',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontWeight: 900
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePermission} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Si es Admin, permite radicar a nombre de otro */}
              {isAdmin && teamMembers.length > 0 && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                    Radicar a nombre de (Colaborador):
                  </label>
                  <select
                    value={targetUserId}
                    onChange={e => setTargetUserId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      fontSize: '12.5px',
                      color: '#0f2a4a',
                      fontWeight: 700,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    <option value="">A mi propio nombre ({user?.firstName} {user?.lastName})</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.firstName} {m.lastName} ({m.email}) - {m.role}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selector de Tipo de Permiso */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '8px' }}>
                  Tipo de Permiso:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                  {[
                    { key: 'CITA_MEDICA', label: 'Cita Médica', icon: '🩺' },
                    { key: 'CALAMIDAD_FAMILIAR', label: 'Calamidad Familiar', icon: '🏠' },
                    { key: 'VACACIONES', label: 'Vacaciones', icon: '🌴' },
                    { key: 'OTRO', label: 'Otro...', icon: '✍️' }
                  ].map(opt => {
                    const isSel = permType === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          setPermType(opt.key as any);
                          if (opt.key === 'VACACIONES') {
                            setHasReposition(false);
                          }
                        }}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '14px',
                          border: isSel ? '2px solid #0070f3' : '1px solid rgba(15, 42, 74, 0.12)',
                          background: isSel ? 'rgba(0, 112, 243, 0.08)' : '#ffffff',
                          color: isSel ? '#0070f3' : '#0f2a4a',
                          fontWeight: 800,
                          fontSize: '12.5px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s'
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{opt.icon}</span>
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Campo de Motivo */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                  {permType === 'OTRO' ? 'Especifica el motivo de tu permiso (escribe aquí):' : 'Motivo o justificación:'}
                </label>
                <input
                  type="text"
                  placeholder={permType === 'OTRO' ? 'Escribe aquí el motivo detallado...' : 'Detalle o justificación (ej: cita odontológica, viaje programado...)'}
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.15)',
                    outline: 'none',
                    fontSize: '13px',
                    color: '#0f2a4a',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* SECCIÓN 1: DÍAS DE AUSENCIA (RANGOS + CALENDARIO 1) */}
              <div style={{
                background: 'rgba(2, 132, 199, 0.04)',
                border: '1.5px solid rgba(2, 132, 199, 0.25)',
                borderRadius: '18px',
                padding: '18px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#0284c7', textTransform: 'uppercase' }}>
                      Calendario 1 · Días de Ausencia / Permiso
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Puedes seleccionar fechas directamente o hacer clic en los días del calendario
                    </div>
                  </div>
                  <span style={{
                    fontSize: '11.5px',
                    fontWeight: 900,
                    background: '#0284c7',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '12px'
                  }}>
                    {selectedPermDays.length} día(s) seleccionado(s)
                  </span>
                </div>

                {/* Selectores de Fecha Rápida */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '4px' }}>
                      Desde:
                    </label>
                    <input
                      type="date"
                      required
                      value={permStartDate}
                      onChange={e => handlePermRangeChange(e.target.value, permEndDate)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '10px',
                        border: '1px solid rgba(2, 132, 199, 0.3)',
                        background: '#ffffff',
                        fontSize: '12.5px',
                        color: '#0f2a4a',
                        fontWeight: 700,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '4px' }}>
                      Hasta:
                    </label>
                    <input
                      type="date"
                      required
                      value={permEndDate}
                      onChange={e => handlePermRangeChange(permStartDate, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '10px',
                        border: '1px solid rgba(2, 132, 199, 0.3)',
                        background: '#ffffff',
                        fontSize: '12.5px',
                        color: '#0f2a4a',
                        fontWeight: 700,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                {/* Navegación del Calendario 1 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (cal1Month === 0) {
                        setCal1Month(11);
                        setCal1Year(cal1Year - 1);
                      } else {
                        setCal1Month(cal1Month - 1);
                      }
                    }}
                    style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontWeight: 800 }}
                  >
                    ‹
                  </button>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                    {MONTH_NAMES[cal1Month]} {cal1Year}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (cal1Month === 11) {
                        setCal1Month(0);
                        setCal1Year(cal1Year + 1);
                      } else {
                        setCal1Month(cal1Month + 1);
                      }
                    }}
                    style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontWeight: 800 }}
                  >
                    ›
                  </button>
                </div>

                {/* Días de la semana */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center', marginBottom: '6px' }}>
                  {DAY_LABELS.map(d => (
                    <span key={d} style={{ fontSize: '10px', fontWeight: 800, color: '#64748b' }}>{d}</span>
                  ))}
                </div>

                {/* Celdas del Calendario 1 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                  {generateMonthGrid(cal1Year, cal1Month).map((c, idx) => {
                    if (!c.isCurrentMonth) {
                      return <div key={`empty-1-${idx}`} style={{ height: '32px' }} />;
                    }
                    const isSelected = selectedPermDays.includes(c.dateStr);
                    return (
                      <button
                        key={`cal1-${c.dateStr}`}
                        type="button"
                        onClick={() => togglePermDay(c.dateStr)}
                        style={{
                          height: '32px',
                          borderRadius: '8px',
                          border: isSelected ? '1.5px solid #0070f3' : '1px solid rgba(15,42,74,0.08)',
                          background: isSelected ? 'linear-gradient(135deg, #0070f3 0%, #0284c7 100%)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#0f2a4a',
                          fontWeight: isSelected ? 900 : 700,
                          fontSize: '11.5px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s'
                        }}
                      >
                        {c.dayNum}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* SECCIÓN 2: DÍAS DE REPOSICIÓN (CALENDARIO 2) */}
              <div style={{
                background: 'rgba(5, 150, 105, 0.04)',
                border: '1.5px solid rgba(5, 150, 105, 0.25)',
                borderRadius: '18px',
                padding: '18px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 900, color: '#059669', textTransform: 'uppercase' }}>
                      Calendario 2 · Días de Reposición (Compensación)
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Indica las fechas en las que se repondrá el tiempo (opcional)
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 800, color: '#0f2a4a', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={hasReposition}
                      onChange={e => setHasReposition(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Aplica reposición</span>
                  </label>
                </div>

                {hasReposition ? (
                  <>
                    {/* Selectores de Fecha de Reposición */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '4px' }}>
                          Fecha Reposición Desde:
                        </label>
                        <input
                          type="date"
                          value={repStartDate}
                          onChange={e => handleRepRangeChange(e.target.value, repEndDate)}
                          style={{
                            width: '100%',
                            padding: '9px 12px',
                            borderRadius: '10px',
                            border: '1px solid rgba(5, 150, 105, 0.3)',
                            background: '#ffffff',
                            fontSize: '12.5px',
                            color: '#0f2a4a',
                            fontWeight: 700,
                            outline: 'none',
                            fontFamily: '"Montserrat", sans-serif',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '4px' }}>
                          Fecha Reposición Hasta:
                        </label>
                        <input
                          type="date"
                          value={repEndDate}
                          onChange={e => handleRepRangeChange(repStartDate, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '9px 12px',
                            borderRadius: '10px',
                            border: '1px solid rgba(5, 150, 105, 0.3)',
                            background: '#ffffff',
                            fontSize: '12.5px',
                            color: '#0f2a4a',
                            fontWeight: 700,
                            outline: 'none',
                            fontFamily: '"Montserrat", sans-serif',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Navegación del Calendario 2 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (cal2Month === 0) {
                            setCal2Month(11);
                            setCal2Year(cal2Year - 1);
                          } else {
                            setCal2Month(cal2Month - 1);
                          }
                        }}
                        style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontWeight: 800 }}
                      >
                        ‹
                      </button>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                        {MONTH_NAMES[cal2Month]} {cal2Year}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          if (cal2Month === 11) {
                            setCal2Month(0);
                            setCal2Year(cal2Year + 1);
                          } else {
                            setCal2Month(cal2Month + 1);
                          }
                        }}
                        style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontWeight: 800 }}
                      >
                        ›
                      </button>
                    </div>

                    {/* Días de la semana */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center', marginBottom: '6px' }}>
                      {DAY_LABELS.map(d => (
                        <span key={d} style={{ fontSize: '10px', fontWeight: 800, color: '#64748b' }}>{d}</span>
                      ))}
                    </div>

                    {/* Celdas del Calendario 2 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                      {generateMonthGrid(cal2Year, cal2Month).map((c, idx) => {
                        if (!c.isCurrentMonth) {
                          return <div key={`empty-2-${idx}`} style={{ height: '32px' }} />;
                        }
                        const isSelected = selectedRepDays.includes(c.dateStr);
                        return (
                          <button
                            key={`cal2-${c.dateStr}`}
                            type="button"
                            onClick={() => toggleRepDay(c.dateStr)}
                            style={{
                              height: '32px',
                              borderRadius: '8px',
                              border: isSelected ? '1.5px solid #059669' : '1px solid rgba(15,42,74,0.08)',
                              background: isSelected ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : '#ffffff',
                              color: isSelected ? '#ffffff' : '#0f2a4a',
                              fontWeight: isSelected ? 900 : 700,
                              fontSize: '11.5px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s'
                            }}
                          >
                            {c.dayNum}
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: '12px' }}>
                      <label style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '4px' }}>
                        Horario o acuerdo de reposición (opcional):
                      </label>
                      <input
                        type="text"
                        placeholder="Ej: Sábado de 8am a 12m, 1 hora diaria al final de la jornada..."
                        value={repositionNotes}
                        onChange={e => setRepositionNotes(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '10px',
                          border: '1px solid rgba(15,42,74,0.15)',
                          fontSize: '12.5px',
                          outline: 'none',
                          fontFamily: '"Montserrat", sans-serif',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#047857', fontStyle: 'italic', background: '#ffffff', padding: '10px 14px', borderRadius: '10px', border: '1px dashed #059669' }}>
                    ✓ No aplica reposición de tiempo para esta solicitud (ej. vacaciones legales, incapacidades o acuerdo previo con Dirección).
                  </div>
                )}
              </div>

              {/* Botones de Envío */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setShowPermModal(false)}
                  style={{
                    padding: '11px 18px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    color: '#0f2a4a',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingPerm}
                  style={{
                    padding: '11px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)',
                    color: '#ffffff',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(0,112,243,0.3)'
                  }}
                >
                  {submittingPerm ? 'Radicando...' : '🚀 Radicar Permiso Oficial'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: ANOTAR HORAS EXTRAS                                                */}
      {/* ========================================================================= */}
      {showOvertimeModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.65)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(28px)',
            borderRadius: '24px',
            border: '1.5px solid rgba(255, 255, 255, 0.98)',
            boxShadow: '0 24px 60px rgba(15, 42, 74, 0.2)',
            width: '100%',
            maxWidth: '520px',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 900, color: '#0f2a4a', margin: 0 }}>
                  Anotar Horas Extras
                </h2>
                <p style={{ fontSize: '11.5px', color: '#64748b', margin: '4px 0 0' }}>
                  Registra tu jornada suplementaria para aprobación y liquidación contable.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowOvertimeModal(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  color: '#64748b',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontWeight: 900
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateOvertime} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                  Fecha de la jornada:
                </label>
                <input
                  type="date"
                  required
                  value={overtimeForm.date}
                  onChange={e => setOvertimeForm({ ...overtimeForm, date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    fontSize: '13px',
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                    Hora Inicio:
                  </label>
                  <input
                    type="time"
                    required
                    value={overtimeForm.startTime}
                    onChange={e => setOvertimeForm({ ...overtimeForm, startTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      fontSize: '13px',
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                    Hora Fin:
                  </label>
                  <input
                    type="time"
                    required
                    value={overtimeForm.endTime}
                    onChange={e => setOvertimeForm({ ...overtimeForm, endTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      fontSize: '13px',
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                  Motivo o Entrega Realizada:
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Detalla las actividades realizadas (campaña, cierre de piezas, urgencia...)"
                  value={overtimeForm.reason}
                  onChange={e => setOvertimeForm({ ...overtimeForm, reason: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    fontSize: '12.5px',
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    resize: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowOvertimeModal(false)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    color: '#0f2a4a',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingOvertime}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: '#ffffff',
                    fontSize: '12.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 6px 16px rgba(5, 150, 105, 0.3)'
                  }}
                >
                  {submittingOvertime ? 'Registrando...' : '⏱️ Guardar Horas Extras'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: DICTAMEN DE NICOLÁS FERNÁNDEZ (PERMISOS)                           */}
      {/* ========================================================================= */}
      {reviewModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.65)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '26px',
            width: '100%',
            maxWidth: '460px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f2a4a', margin: '0 0 6px' }}>
              {reviewAction === 'APROBADO' ? '✅ Aprobar Permiso Laboral' : '❌ Rechazar Permiso Laboral'}
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px' }}>
              {reviewAction === 'APROBADO'
                ? 'Al aprobar, el permiso pasará de inmediato al radar contable de Alejandra Fernández.'
                : 'Indica las razones para que el colaborador esté informado.'}
            </p>

            <label style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
              Observaciones del dictamen (opcional):
            </label>
            <textarea
              rows={3}
              placeholder="Notas para el colaborador o para contabilidad..."
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(15,42,74,0.15)',
                fontSize: '12px',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                resize: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setReviewModalOpen(false)}
                style={{
                  padding: '9px 15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(15,42,74,0.15)',
                  background: '#ffffff',
                  color: '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleNicolasReview}
                disabled={submittingReview}
                style={{
                  padding: '9px 18px',
                  borderRadius: '10px',
                  border: 'none',
                  background: reviewAction === 'APROBADO' ? '#059669' : '#dc2626',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                {submittingReview ? 'Procesando...' : reviewAction === 'APROBADO' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: RADAR DE ALEJANDRA FERNÁNDEZ (PERMISOS)                           */}
      {/* ========================================================================= */}
      {radarModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.65)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '26px',
            width: '100%',
            maxWidth: '460px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f2a4a', margin: '0 0 6px' }}>
              📌 Confirmar en Radar de Talento Humano
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px' }}>
              Hola Alejandra, confirma que este permiso ha sido integrado a tu control contable y de nómina.
            </p>

            <label style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
              Notas contables o de verificación:
            </label>
            <textarea
              rows={3}
              placeholder="Ej: Verificado y programado para control de nómina y asistencia..."
              value={radarNotes}
              onChange={e => setRadarNotes(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(15,42,74,0.15)',
                fontSize: '12px',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                resize: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setRadarModalOpen(false)}
                style={{
                  padding: '9px 15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(15,42,74,0.15)',
                  background: '#ffffff',
                  color: '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAlejandraRadarConfirm}
                disabled={submittingRadar}
                style={{
                  padding: '9px 18px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#0284c7',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                {submittingRadar ? 'Guardando...' : 'Confirmar en Radar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: DICTAMEN DE HORAS EXTRAS (NICOLÁS / ADMIN)                         */}
      {/* ========================================================================= */}
      {overtimeReviewModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 42, 74, 0.65)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '26px',
            width: '100%',
            maxWidth: '460px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f2a4a', margin: '0 0 6px' }}>
              {overtimeReviewAction === 'APROBADO' ? '✅ Aprobar Horas Extras' : '❌ Rechazar Horas Extras'}
            </h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px' }}>
              {overtimeReviewAction === 'APROBADO'
                ? 'Al aprobar, las horas se liquidarán automáticamente en contabilidad para Alejandra Fernández.'
                : 'Indica el motivo de rechazo para el colaborador.'}
            </p>

            <label style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
              Observaciones (opcional):
            </label>
            <textarea
              rows={3}
              placeholder="Notas o justificación..."
              value={overtimeReviewNotes}
              onChange={e => setOvertimeReviewNotes(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(15,42,74,0.15)',
                fontSize: '12px',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                resize: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setOvertimeReviewModalOpen(false)}
                style={{
                  padding: '9px 15px',
                  borderRadius: '10px',
                  border: '1px solid rgba(15,42,74,0.15)',
                  background: '#ffffff',
                  color: '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleOvertimeReview}
                disabled={submittingOvertimeReview}
                style={{
                  padding: '9px 18px',
                  borderRadius: '10px',
                  border: 'none',
                  background: overtimeReviewAction === 'APROBADO' ? '#059669' : '#dc2626',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                {submittingOvertimeReview ? 'Procesando...' : overtimeReviewAction === 'APROBADO' ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermisosHorasExtra;
